import type { CSSProperties, ElementType, ReactNode } from 'react'
import { useReveal } from '@/hooks'
import { cn } from '@/lib/cn'

interface RevealProps {
  children: ReactNode
  as?: ElementType
  /** Stagger in milliseconds, for revealing siblings in sequence. */
  delay?: number
  className?: string
}

/**
 * Fades and lifts its children into place the first time they scroll into
 * view. Under `prefers-reduced-motion` the reveal classes collapse to a
 * no-op in `effects.css`, so content is never hidden from a visitor who has
 * asked for less movement.
 */
export const Reveal = ({
  children,
  as: Component = 'div',
  delay = 0,
  className,
}: RevealProps) => {
  const { ref, isVisible } = useReveal<HTMLDivElement>()

  return (
    <Component
      ref={ref}
      className={cn('reveal', isVisible && 'revealVisible', className)}
      style={delay ? ({ transitionDelay: `${delay}ms` } as CSSProperties) : undefined}
    >
      {children}
    </Component>
  )
}
